//CG: Face mutabil un panou pozitionat absolut, prin drag pe un element "handle" (de regula antetul).
// Pozitia se scrie inline in px pe panou, peste valorile din CSS; dublu-clic pe handle revine la CSS.

const DEFAULT_MIN_VISIBLE = 60;
const DEFAULT_IGNORE = 'button, input, select, textarea, a, .k-widget';

export default function makePanelDraggable(panel, handle, options) {
    if (panel == null || handle == null) {
        return null;
    }

    const opts = options || {};
    const ignoreSelector = opts.ignoreSelector != null ? opts.ignoreSelector : DEFAULT_IGNORE;
    const minVisible = opts.minVisible != null ? opts.minVisible : DEFAULT_MIN_VISIBLE;

    let pointerId = null;
    let grabX = 0;
    let grabY = 0;

    handle.style.cursor = 'move';
    handle.style.userSelect = 'none';
    handle.style.touchAction = 'none';

    // Limitele in care poate fi mutat panoul (implicit parintele pozitionat).
    const getBoundsRect = () => {
        const el = typeof opts.bounds === 'function' ? opts.bounds() : opts.bounds;
        return (el || panel.offsetParent || document.documentElement).getBoundingClientRect();
    };

    // Cutia vizibila folosita la limitare: panoul poate fi un wrapper fara dimensiuni proprii
    // (copil absolut => fit-content = 0), caz in care limitam dupa continut.
    const getContentRect = (panelRect) => {
        const el = typeof opts.content === 'function' ? opts.content() : opts.content;
        if (el == null) {
            return panelRect;
        }
        const rect = el.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0 ? rect : panelRect;
    };

    // Muta coltul stanga-sus al panoului in (clientX, clientY), pastrand minVisible px din continut in harta.
    const moveTo = (clientX, clientY) => {
        const parent = panel.offsetParent;
        if (parent == null) {
            return;
        }

        const parentRect = parent.getBoundingClientRect();
        const panelRect = panel.getBoundingClientRect();
        const contentRect = getContentRect(panelRect);
        const bounds = getBoundsRect();

        // decalajul continutului fata de originea panoului, ca sa limitam ce se vede efectiv
        const dx = contentRect.left - panelRect.left;
        const dy = contentRect.top - panelRect.top;

        const minX = bounds.left - contentRect.width + minVisible - dx;
        const maxX = bounds.right - minVisible - dx;
        const minY = bounds.top - dy;
        const maxY = bounds.bottom - minVisible - dy;

        const x = Math.min(Math.max(clientX, minX), maxX);
        const y = Math.min(Math.max(clientY, minY), maxY);

        panel.style.left = (x - parentRect.left - parent.clientLeft) + 'px';
        panel.style.top = (y - parentRect.top - parent.clientTop) + 'px';
        panel.style.right = 'auto';
        panel.style.bottom = 'auto';
    };

    const onPointerDown = (evt) => {
        if (evt.button != null && evt.button !== 0) {
            return;
        }
        if (evt.target != null && evt.target.closest != null && evt.target.closest(ignoreSelector) != null) {
            return;
        }

        const panelRect = panel.getBoundingClientRect();
        grabX = evt.clientX - panelRect.left;
        grabY = evt.clientY - panelRect.top;
        pointerId = evt.pointerId;

        // preluam complet pozitionarea panoului
        panel.style.margin = '0';
        handle.setPointerCapture(pointerId);
        handle.classList.add('xp-panel-dragging');
        evt.preventDefault();
    };

    const onPointerMove = (evt) => {
        if (pointerId == null || evt.pointerId !== pointerId) {
            return;
        }
        moveTo(evt.clientX - grabX, evt.clientY - grabY);
    };

    const onPointerEnd = (evt) => {
        if (pointerId == null || evt.pointerId !== pointerId) {
            return;
        }
        if (handle.hasPointerCapture(pointerId)) {
            handle.releasePointerCapture(pointerId);
        }
        pointerId = null;
        handle.classList.remove('xp-panel-dragging');
    };

    // Revine la pozitia din CSS.
    const reset = () => {
        panel.style.left = '';
        panel.style.top = '';
        panel.style.right = '';
        panel.style.bottom = '';
        panel.style.margin = '';
    };

    // Readuce panoul in limite, dupa redimensionarea hartii sau la redeschidere.
    const clamp = () => {
        if (panel.style.left === '' || panel.offsetParent == null) {
            return;
        }
        const panelRect = panel.getBoundingClientRect();
        moveTo(panelRect.left, panelRect.top);
    };

    handle.addEventListener('pointerdown', onPointerDown);
    handle.addEventListener('pointermove', onPointerMove);
    handle.addEventListener('pointerup', onPointerEnd);
    handle.addEventListener('pointercancel', onPointerEnd);
    handle.addEventListener('dblclick', reset);

    const destroy = () => {
        handle.removeEventListener('pointerdown', onPointerDown);
        handle.removeEventListener('pointermove', onPointerMove);
        handle.removeEventListener('pointerup', onPointerEnd);
        handle.removeEventListener('pointercancel', onPointerEnd);
        handle.removeEventListener('dblclick', reset);
        reset();
    };

    return { reset, clamp, destroy };
}
